import {useAppDispatch, useAppSelector} from '@/store'
import {setPointMultiplierFormData} from '@/store/slices/pointMultiplier'
import {setRuleSet} from '@/store/slices/salesTargetSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants'
import { Constants, DATE_VALIDATION_TYPE } from '@/utils/constants/commonConstants'

export const useCSVFileValidation = () => {
  const value = []
  const errors = []
  const dispatch = useAppDispatch()
  const salesTarget = useAppSelector(state => state.salesTarget)

  // Define constants outside switch statement
  const alphanumericRegex = /^[a-z0-9]+$/i

  // Helper function to validate file type
  const validateFileType = (expectedType, actualType) => {
    if (expectedType !== actualType) {
      errors.push(`file type should be ${expectedType}`)
    }
  }

  // Helper function to clean CSV data
  const cleanCSVData = (csvData) => {
    return csvData
      ?.filter(row => {
        return Object.values(row).some(val => val !== undefined && val !== null && String(val).trim() !== '')
      })
      .map(row => {
        const cleanRow = {}
        Object.entries(row).forEach(([key, val]) => {
          key = String(key).trim()
          cleanRow[key] = val !== undefined && val !== null ? String(val).trim() : ''
        })
        return cleanRow
      })
  }

  // Helper function to validate headers
  const validateHeaders = (headers, validHeaders, requiredHeaders) => {
    if (validHeaders?.length > 0) {
      const unexpectedHeaders = headers.filter(header => !validHeaders.includes(header))
      const validUnexpectedHeaders = unexpectedHeaders.filter(header => {
        return header.trim() !== '' && !header.startsWith('_')
      })
      if (validUnexpectedHeaders.length > 0) {
        errors.push(`Unexpected column(s) found: ${validUnexpectedHeaders.join(', ')}`)
      }
    }

    if (requiredHeaders?.length > 0) {
      const missingHeaders = requiredHeaders.filter(header => !headers.includes(header))
      if (missingHeaders.length > 0) {
        errors.push(`Missing required column(s): ${missingHeaders.join(', ')}`)
      }
    }
  }

  // Helper function to validate start date conditions
  const validateStartDateConditions = (inputDate, currentDate, columnName, rowIndex, columns, row) => {
    // Check if this is a new record (no ID column means it's a new record)
    const isNewRecord = !row['ID'] && !row['id'] && !row['Id']
    
    // For new records: Start date cannot be in the past
    if (isNewRecord && inputDate < currentDate) {
      errors.push(`Row ${rowIndex + 2}: '${columnName}' cannot be in the past for new records.`)
      return false
    }
    
    // Check against end date
    const endDateColumn = columns.find(col => col.dateType === DATE_VALIDATION_TYPE.END_DATE)
    if (endDateColumn) {
      const endDateValue = row[endDateColumn.name]?.trim()
      if (endDateValue) {
        const endDate = new Date(endDateValue)
        if (!isNaN(endDate.getTime())) {
          if (inputDate > endDate) {
            errors.push(`Row ${rowIndex + 2}: '${columnName}' cannot be future than End Date.`)
            return false
          }
          if (inputDate.getTime() === endDate.getTime()) {
            errors.push(`Row ${rowIndex + 2}: Start Date and End Date cannot be the same.`)
            return false
          }
        }
      }
    }
    
    return true
  }

  // Helper function to validate end date conditions
  const validateEndDateConditions = (inputDate, currentDate, columnName, rowIndex, columns, row) => {
    // End date should not be in the past (for all records)
    if (inputDate < currentDate) {
      errors.push(`Row ${rowIndex + 2}: '${columnName}' cannot be in the past.`)
      return false
    }
    
    // Check against start date
    const startDateColumn = columns.find(col => col.dateType === DATE_VALIDATION_TYPE.START_DATE)
    if (startDateColumn) {
      const startDateValue = row[startDateColumn.name]?.trim()
      if (startDateValue) {
        const startDate = new Date(startDateValue)
        if (!isNaN(startDate.getTime())) {
          if (inputDate < startDate) {
            errors.push(`Row ${rowIndex + 2}: '${columnName}' cannot be past than Start Date.`)
            return false
          }
          if (inputDate.getTime() === startDate.getTime()) {
            errors.push(`Row ${rowIndex + 2}: End Date and Start Date cannot be the same.`)
            return false
          }
        }
      }
    }
    
    return true
  }

  // Helper function to validate date fields
  const validateDateField = (columnValue, columnName, rowIndex, dateType, columns, row) => {
    if (!columnValue) return true
    
    // Check if date contains time (should only accept YYYY-MM-DD format)
    const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/
    if (!dateOnlyRegex.test(columnValue)) {
      errors.push(`Row ${rowIndex + 2}: '${columnName}' must be in YYYY-MM-DD format.`)
      return false
    }
    
    const currentDate = new Date()
    const inputDate = new Date(columnValue)
    // Check if date format is proper - isNaN(inputDate.getTime()) is the correct way to check for Invalid Date
    if (isNaN(inputDate.getTime())) {
      errors.push(`Row ${rowIndex + 2}: '${columnName}' must be a valid date format.`)
      return false
    }
    
    if (dateType === DATE_VALIDATION_TYPE.START_DATE) {
      return validateStartDateConditions(inputDate, currentDate, columnName, rowIndex, columns, row)
    }
    
    if (dateType === DATE_VALIDATION_TYPE.END_DATE) {
      return validateEndDateConditions(inputDate, currentDate, columnName, rowIndex, columns, row)
    }

    return true
  }

  // Helper function to validate unique constraint
  const validateUniqueConstraint = (columnValue, columnName, rowIndex, unique, csvData) => {
    if (unique === true && columnValue) {
      // Filter all rows that have the same value for this specific column
      const duplicateRows = csvData.filter(row => {
        const rowValue = row[columnName]?.trim()
        return rowValue === columnValue
      })
      
      // If more than one row has this value, it's a duplicate
      if (duplicateRows.length > 1) {
        errors.push(`Row ${rowIndex + 2}: '${columnName}' must be unique. Found duplicate value: ${columnValue}.`)
        return false
      }
    }
    return true
  }

  // Helper function to process point multiplier data
  const processPointMultiplierData = (cleanedData) => {
    const toCamelCase = str => {
      return str
        .toLowerCase()
        .split(/[\s]+/)
        .map((word, index) => {
          if (index === 0) return word
          return word.charAt(0).toUpperCase() + word.slice(1)
        })
        .join('')
    }

    const productMultipliers = cleanedData.map(row => {
      Object.keys(row).forEach(key => {
        const newKey = toCamelCase(key)
        if (newKey !== key) {
          row[newKey] = row[key]
          delete row[key]
        }
      })
      return row
    })
    dispatch(setPointMultiplierFormData({productMultipliers}))
  }

  // Helper function to dispatch rule set
  const dispatchRuleSet = () => {
    const targetName = salesTarget?.targetType === TARGET_TYPES.MainTarget
    const ruleSet = {
      includeAllProductsByDefault: targetName,
      skipNonAssorted: false,
      rules: [{
        includedProductIds: value?.map(v => v?.toString()),
        includedCategories: [],
        excludedCategories: [],
        excludedProductIds: [],
      }],
    }
    dispatch(setRuleSet({ruleSet}))
  }

  const validateFieldType = (type, columnValue, columnName, rowIndex, minChar, maxChar, rawValue) => {
    switch (type) {
      case 'string': {
        if (typeof rawValue !== 'string') {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be a string.`)
        }
        if (columnValue && (columnValue.length < minChar || columnValue.length > maxChar)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be between ${minChar} and ${maxChar} characters.`)
        }
        break
      }
      case 'number': {
        if (isNaN(Number(columnValue))) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be a numerical value.`)
        }
        break
      }
      case 'alphanumeric': {
        if (!alphanumericRegex.test(columnValue)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be an alphanumeric string.`)
        }
        if (columnValue && (columnValue.length < minChar || columnValue.length > maxChar)) {
          errors.push(`Row ${rowIndex + 2}: '${columnName}' should be between ${minChar} and ${maxChar} characters.`)
        }
        break
      }
    }
  }

  // Helper function to validate a single column in a row
  const validateRowColumn = (column, row, rowIndex, columns, cleanedData) => {
    const {name: columnName, type, required, minChar, maxChar, unique, dateType} = column
    const rawValue = row[columnName]
    const columnValue = rawValue?.trim()

    // Handle date validation
    if (type === Constants.Date) {
      return validateDateField(columnValue, columnName, rowIndex, dateType, columns, row)
    }

    // Handle required field validation
    if (required && !columnValue) {
      errors.push(`Row ${rowIndex + 2}: '${columnName}' is required.`)
      return false
    }

    // Handle unique constraint validation
    if (!validateUniqueConstraint(columnValue, columnName, rowIndex, unique, cleanedData)) {
      return false
    }

    // Skip empty optional fields
    if (!columnValue && !required) return true

    // Validate field type
    validateFieldType(type, columnValue, columnName, rowIndex, minChar, maxChar, rawValue)
    return true
  }

  // Helper function to process a single row
  const processRow = (row, rowIndex, columns, desiredKey, cleanedData) => {
    if (desiredKey && row[desiredKey]) {
      value.push(row[desiredKey].trim())
    }

    // Process each column in the row
    columns?.forEach(column => {
      validateRowColumn(column, row, rowIndex, columns, cleanedData)
    })
  }

  // Helper function to handle route-specific processing
  const handleRouteProcessing = (route, cleanedData) => {
    if (!route) {
      dispatchRuleSet()
    } else if (route === ROUTES.POINT_MULTIPLIER_PRODUCTS) {
      processPointMultiplierData(cleanedData)
    }
  }

  const validateCSVFile = (fileValidationRules, fileObj, desiredKey, route = false) => {
    const {type, columns, requiredHeaders} = fileValidationRules
    const {fileType, csvData} = fileObj
    const validHeaders = columns.map(column => column.name)

    // Validate file type
    validateFileType(type, fileType)

    // Clean and validate CSV data
    const cleanedData = cleanCSVData(csvData)
    if (!cleanedData?.length) {
      errors.push('CSV file contains no valid data')
      return {isInvalidFileData: true, errorsList: errors}
    }

    // Validate headers
    const headers = Object.keys(cleanedData[0])
    validateHeaders(headers, validHeaders, requiredHeaders)

    // Process each row
    cleanedData.forEach((row, rowIndex) => {
      processRow(row, rowIndex, columns, desiredKey, cleanedData)
    })

    // Handle route-specific processing
    handleRouteProcessing(route, cleanedData)

    return {isInvalidFileData: errors.length > 0, errorsList: errors}
  }

  return {validateCSVFile}
}
